//
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import queryString from 'query-string';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { datasetsActions, datasetsSelectors } from 'app-components/flux/datasets';

import { setShareAcceptData } from 'data/app/helpers';
import { mkShareAcceptData } from 'data/app/types';

// Query Parameters
//   - granteeEmail
//   - new (is grantee email a new user - e.g. requires create)
//   - datasetId
//
// Case 1: User Exists
//   Case 1a: A User is Currently Signed-In and Is Same as Grantee
//     - Switch Datasets
//   Case 1b:
//     - Add sign-in variables to local-storage
//     Case 1b1: User is Signed In
//       - Logout (which will re-direct to sign-in afterwards)
//     Case 1a2: User is Not Signed In
//       - Redirect to '/' to force sign-in
// Case 2: User Needs to be Created
//   - Add create variables to local-storage
//   Case 2a: A user is Currently Signed In
//     - Logout (which will re-direct to sign-in afterwards)
//   Case 2b: A user is Not Signed In
//     - Redirect to '/' to force sign-in

const useShareAccept = () => {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(authSelectors.isAuthenticated);

  const authQcsId = useSelector(authSelectors.getQCSId);
  const datasetsById = useSelector(datasetsSelectors.getResourcesById);

  const startNewSignInOrSignUp = useCallback(() => {
    // Note: action must cause a hard refresh to reload the AuthDialog component
    if (isAuthenticated) {
      // Case 1b1 or 2a: User is signed-in, so logout to start new sign-in process
      dispatch(authActions.authLogout({ reason: 'ACCEPT_SHARE' }, { context: 'dataset sharing' }));
    } else {
      // Case 1b2 or 2b: User is not signed-in, so just redirect to root page to start new sign-in process
      window.location.href = '/';
    }
  }, [dispatch, isAuthenticated]);

  const fetchAndSelectDataset = useCallback(
    async (datasetId) => {
      const fetchDatasets = actionHelpers.bindPromiseAction(dispatch, datasetsActions.getDatasets);
      const datasets = await fetchDatasets();

      const dataset = datasets.find((d) => d.id === datasetId);
      if (dataset) {
        authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset }));
      } else {
        // log.error('could not find dataset after fetching', datasetId, datasets);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const query = queryString.parse(window.location.search);
    const { datasetId, granteeEmail, granteeId } = query;
    if (granteeEmail && datasetId) {
      if (granteeId) {
        // Case 1: User Exists
        if (granteeId && isAuthenticated && granteeId === authQcsId) {
          // Case 1a: A User is Currently Signed-In and Is Same as Grantee
          const sharedDataset = datasetsById.get(datasetId);
          if (sharedDataset) {
            authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset: sharedDataset }));
          } else {
            // noinspection JSIgnoredPromiseFromCall
            fetchAndSelectDataset(datasetId);
          }
        } else {
          // Case 1b: User is not authenticated or user is different from authenticated user
          setShareAcceptData(mkShareAcceptData({ granteeEmail: decodeURIComponent(granteeEmail), datasetId }));
          startNewSignInOrSignUp();
        }
      } else {
        // Case 2: User needs to be created
        setShareAcceptData(
          mkShareAcceptData({ isGranteeNewUser: true, granteeEmail: decodeURIComponent(granteeEmail), datasetId }),
        );
        startNewSignInOrSignUp();
      }
    } else {
      // TODO: What to do about this corner case?
    }
  }, [authQcsId, datasetsById, fetchAndSelectDataset, isAuthenticated, startNewSignInOrSignUp]);

  return null;
};

export const Component = useShareAccept;
export default useShareAccept;
